import type React from "react";
import { useState } from "react";
import { ChevronDown, ChevronUp, Folder } from "lucide-react";
import { ImageLoader } from "~/components/ImageLoader";
import { Tooltip } from "~/components/Tooltip";
import { RWA_COIN_ICON } from "~/consts";
import { cn } from "~/lib/utils";
import { Aum30dGrowthIndicator } from "./Aum30dGrowthIndicator";
import { buildProductPreviewText } from "./productPreview";
import * as Analytics from "../../analytics";
import type { ScreenerAsset } from "./types";
import { DataRoomAccessModal, DataRoomAccessMode } from "./DataRoomAccessModal";
import { AssetResultRowDetails } from "./AssetResultRowDetails";
import {
    formatHoldersCount,
    formatMinInvestment,
    formatFee,
    formatList,
    formatNetworks,
    formatLiquidity,
    formatAUM,
} from "./functions";
import { assetHasCorpus, getCorpusAssetId } from "~/modules/asset_chat/assets";
import { useAssetChat } from "~/modules/asset_chat/hooks/useAssetChat";
import { formatAssetReturn } from "./assetPerformance";
import { Link } from "~/components/Link";
import { getAssetURL } from "~/utils/getAssetURL";
import { AssetScreenerUserEvents } from "~/modules/analytics/events/asset-screener";

// // // //

/** Blue data-room pill treatment shared by the per-row badge below and the
 * screener section header's tag, so the two can't drift apart visually. */
export const dataRoomPillClassName =
    "inline-flex h-6 items-center gap-1 border-blue-200 bg-primary-foreground px-2.5 text-[11px] font-medium leading-none text-blue-700";

/** Badge shown when an asset has a data room available. Clicking it expands
 * the asset card and opens a new AI chat for the deal. */
function DataRoomAvailableButton(props: {
    assetName: string;
    isExpanded: boolean;
    onClick: () => void;
}) {
    const { assetName, isExpanded, onClick } = props;

    return (
        <button
            type="button"
            onClick={onClick}
            aria-expanded={isExpanded}
            aria-label={`Open ${assetName} details and data room chat`}
            className={cn(
                dataRoomPillClassName,
                "absolute top-0 left-1/2 z-10 -translate-x-1/2 cursor-pointer rounded-b-md border-x border-b shadow-xs transition-colors hover:border-blue-300 hover:bg-primary-light hover:text-blue-800 focus-visible:ring-2 focus-visible:ring-blue-300 focus-visible:ring-offset-2 focus-visible:outline-none",
            )}
        >
            <span className="whitespace-nowrap">Open Data Room Chat</span>
            <Folder aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
        </button>
    );
}

// // // //

function SummaryMetric(props: {
    value: React.ReactNode;
    detail?: string;
    align?: "left" | "right";
    accent?: boolean;
    className?: string;
}) {
    const { value, detail, align = "left", accent = false, className } = props;

    return (
        <div
            className={cn(
                "min-w-0",
                align === "right" ? "text-right" : "text-left",
                className,
            )}
        >
            <p
                className={cn(
                    "text-base font-medium leading-tight tracking-tight tabular-nums",
                    accent ? "text-green-700" : "text-foreground",
                )}
            >
                {value}
            </p>
            {detail && (
                <p className="mt-1 text-xs font-normal leading-tight text-tertiary-foreground">
                    {detail}
                </p>
            )}
        </div>
    );
}

// // // //

export function AssetResultRow(props: {
    asset: ScreenerAsset;
    isExpanded: boolean;
    hasTopBorder: boolean;
    isFirstRow: boolean;
    isLastRow: boolean;
    isLoggedIn: boolean;
    onToggle: () => void;
}) {
    const {
        asset,
        isExpanded,
        hasTopBorder,
        isFirstRow,
        isLastRow,
        isLoggedIn,
        onToggle,
    } = props;

    const { openNewChat } = useAssetChat();

    // Wrap the parent-provided toggle so we can record whether the user is
    // expanding or collapsing, and which control they used to do it.
    const handleToggle = (
        toggle_source: "chevron_button" | "data_room_badge",
    ) => {
        if (isExpanded) {
            Analytics.trackAssetRowCollapse({
                asset_id: asset.id,
                asset_ticker: asset.symbol,
                toggle_source,
            });
        } else {
            Analytics.trackAssetRowExpand({
                asset_id: asset.id,
                asset_ticker: asset.symbol,
                toggle_source,
            });
        }

        onToggle();
    };

    const rowMetrics = {
        aum: formatAUM(asset.stats.aum),
        investors: formatHoldersCount(asset.stats.holdersCount),
        redemption: formatLiquidity(asset.redemptionFrequency.label),
        minInvestment: formatMinInvestment(asset.minInvestment),
        networks: formatNetworks(asset.networks),
        eligibility: formatList(asset.stats.investorTypes),
        fee: formatFee(asset.stats.fee),
        performanceFee: formatFee(asset.stats.performanceFee),
        subscriptionFee: formatFee(asset.stats.subscriptionFee),
        redemptionFee: formatFee(asset.stats.redemptionFee),
    };

    const [showDataRoomModal, setShowDataRoomModal] = useState(false);

    const [dataRoomAccessMode, _setDataRoomAccessMode] =
        useState<DataRoomAccessMode>("data-room");

    const factsheetDocument =
        asset.dataRoomDocuments.find((document) => {
            return /fact\s*sheet|factsheet|1-pager|one-pager/i.test(
                `${document.name} ${document.url}`,
            );
        }) ?? null;

    const shortProductDescription = buildProductPreviewText({
        text: asset.productPreview ?? asset.dealBlurb ?? asset.description,
        productNames: [asset.name, asset.symbol],
    });

    // The "Data Room Available" badge tracks corpus availability only (same source as
    // the screener's data-room filter). The AI chat inside the expanded row is what
    // additionally gates on the enable-asset-chat feature flag.
    const hasDataRoom = assetHasCorpus(asset);

    // Clicking the badge opens (never collapses) the asset card and starts a
    // fresh AI chat for the deal in the global dock.
    const handleDataRoomBadgeClick = () => {
        if (!isExpanded) {
            handleToggle("data_room_badge");
        }

        const corpusAssetId = getCorpusAssetId(asset);
        if (corpusAssetId && isLoggedIn) {
            openNewChat({
                assetCorpusId: corpusAssetId,
                assetName: asset.symbol,
            });
        }
    };

    return (
        <article
            // Identifies which asset a row belongs to in the DOM. The listing
            // page scrolls through the virtualizer rather than by query, so
            // this is a handle for tests and debugging the virtualized list.
            data-asset-id={asset.id}
            className={cn(
                "relative overflow-hidden bg-card transition-[margin,border-radius] duration-250 ease-in-out",
                isExpanded
                    ? "my-2 rounded-lg border"
                    : cn("my-0 rounded-none border-x border-b", {
                          "border-t": hasTopBorder,
                          "rounded-t-lg": isFirstRow,
                          "rounded-b-lg": isLastRow,
                      }),
            )}
        >
            {hasDataRoom && (
                <DataRoomAvailableButton
                    assetName={asset.name}
                    isExpanded={isExpanded}
                    onClick={handleDataRoomBadgeClick}
                />
            )}
            <div
                role="button"
                onClick={() => handleToggle("chevron_button")}
                className="flex flex-col gap-4 p-5 xl:grid xl:grid-cols-[4rem_minmax(19rem,3.05fr)_0.7fr_0.7fr_0.8fr_0.5fr_2.25rem] xl:items-center xl:px-6"
            >
                {/* Header — logo beside name on mobile; flattens into the
                    desktop grid via xl:contents so the desktop layout is
                    unchanged. pr-9 reserves room for the floating chevron. */}
                <div className="flex items-start gap-3 pr-9 xl:contents xl:pr-0">
                    <div className="flex flex-col items-center gap-2.5">
                        <ImageLoader
                            src={
                                asset.icon ||
                                asset.platform?.icon_url ||
                                RWA_COIN_ICON
                            }
                            alt={`${asset.name} Logo`}
                            fallbackSrc={RWA_COIN_ICON}
                            className="size-10 shrink-0 rounded-md object-cover"
                        />
                        {asset.symbol && (
                            <span
                                className="max-w-14 truncate rounded bg-muted px-1 py-0.5 font-mono text-[10px] font-normal leading-none text-tertiary-foreground/75"
                                title={asset.symbol}
                            >
                                {asset.symbol}
                            </span>
                        )}
                    </div>

                    <div className="flex min-w-0 flex-1 items-center gap-3 xl:block">
                        <div className="min-w-0">
                            <div className="flex min-w-0 items-center gap-1.5">
                                <Link
                                    href={getAssetURL({
                                        asset: {
                                            ticker: asset.symbol,
                                            slug: null,
                                        },
                                    })}
                                    aria-label={`View ${asset.name} asset`}
                                    analyticsLabel={
                                        AssetScreenerUserEvents.AssetCardClickViewAsset
                                    }
                                    analyticsEventProperties={{
                                        asset_ticker: asset.symbol,
                                    }}
                                    className="line-clamp-2 min-w-0 text-base font-medium leading-tight tracking-tight text-foreground hover:text-link-hover flex items-center gap-1"
                                >
                                    {asset.name}
                                </Link>
                                {asset.isNonKyc && (
                                    <Tooltip
                                        placement="top"
                                        tooltipWrapperClassName="border border-muted bg-card px-2.5 py-2 text-sm text-secondary-foreground shadow-lg rounded-lg"
                                        tooltipContent={
                                            <div className="max-w-xs">
                                                <p className="font-medium">
                                                    Purchasable without KYC
                                                </p>
                                                <p className="mt-1 text-secondary-foreground">
                                                    The token is transferable
                                                    without restrictions, or
                                                    with only a sanctions
                                                    blacklist — so any wallet
                                                    can acquire it (e.g. on a
                                                    DEX) without identity
                                                    verification.
                                                </p>
                                            </div>
                                        }
                                    >
                                        {({ setTriggerRef }) => (
                                            <span
                                                ref={setTriggerRef}
                                                className="shrink-0 cursor-help rounded bg-emerald-100 px-1.5 py-0.5 text-[10px] font-medium leading-none text-emerald-800"
                                            >
                                                Non-KYC
                                            </span>
                                        )}
                                    </Tooltip>
                                )}
                            </div>
                            {shortProductDescription && (
                                <p className="mt-1 line-clamp-2 text-[13px] leading-5 text-secondary-foreground">
                                    {shortProductDescription}
                                </p>
                            )}
                            <div className="mt-1.5 flex min-w-0 items-center gap-1 text-xs text-tertiary-foreground">
                                <span
                                    className="truncate"
                                    title={asset.assetClass.name}
                                >
                                    {asset.assetClass.name}
                                </span>
                                <span aria-hidden="true">·</span>
                                <span
                                    className="truncate"
                                    title={asset.platform?.name ?? "—"}
                                >
                                    {asset.platform?.name ?? "—"}
                                </span>
                                <span aria-hidden="true">·</span>
                                <span
                                    title={asset.jurisdiction.name}
                                    className="truncate"
                                >
                                    {asset.jurisdiction.name}
                                </span>
                                {asset.jurisdiction.icon && (
                                    <ImageLoader
                                        src={asset.jurisdiction.icon}
                                        alt={`${asset.jurisdiction.name} flag`}
                                        fallbackSrc={RWA_COIN_ICON}
                                        className="h-3 w-4 shrink-0 rounded-[2px] object-cover opacity-80"
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Metrics — 2x2 grid on mobile; xl:contents flattens this
                    wrapper (and the nested contents wrapper) so each metric
                    lands in its own desktop grid column, unchanged. */}
                <div className="grid grid-cols-2 gap-x-4 gap-y-3 xl:contents">
                    <div className="contents">
                        <SummaryMetric
                            value={formatAssetReturn(
                                asset.returns.trailing1Year,
                            )}
                            detail="1Y Return"
                        />
                        <SummaryMetric
                            value={formatAssetReturn(
                                asset.returns.trailing1Month,
                            )}
                            detail="1M Return"
                        />
                    </div>
                    <SummaryMetric
                        value={
                            <span className="inline-flex max-w-full flex-row flex-nowrap items-baseline justify-start gap-x-1">
                                <span className="min-w-0 truncate">
                                    {rowMetrics.aum}
                                </span>
                                <Aum30dGrowthIndicator
                                    pct={asset.stats.aum30dGrowthPct}
                                />
                            </span>
                        }
                        detail="AUM"
                    />
                    <SummaryMetric
                        value={rowMetrics.redemption}
                        detail="Redemption"
                        className="xl:text-right"
                    />
                </div>

                {/* Chevron — floated to the top-right on mobile; xl:static
                    returns it to its desktop grid column. */}
                <div className="absolute top-3 right-3 z-10 flex items-center justify-end xl:static xl:z-auto xl:justify-self-end">
                    <button
                        type="button"
                        // onClick={() => handleToggle("chevron_button")}
                        aria-expanded={isExpanded}
                        aria-label={
                            isExpanded ? "Collapse asset" : "Expand asset"
                        }
                        className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-secondary-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                        {isExpanded && <ChevronUp className="size-5" />}
                        {!isExpanded && <ChevronDown className="size-5" />}
                    </button>
                </div>
            </div>

            <AssetResultRowDetails
                asset={asset}
                isExpanded={isExpanded}
                isLoggedIn={isLoggedIn}
            />

            <DataRoomAccessModal
                asset={asset}
                show={showDataRoomModal}
                onHide={() => setShowDataRoomModal(false)}
                mode={dataRoomAccessMode}
                documentOverride={
                    dataRoomAccessMode === "factsheet"
                        ? factsheetDocument
                        : null
                }
            />
        </article>
    );
}
